import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, ExternalLink } from 'lucide-react';
import api from '@/lib/api';

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (e?.message || '저장 실패');
};

const Field = ({ label, value, onChange, rows, testid, max }) => (
  <label className="block">
    <span className="text-xs font-bold text-gray-700">{label}</span>
    {rows ? (
      <textarea rows={rows} value={value || ''} maxLength={max} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
    ) : (
      <input value={value || ''} maxLength={max} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
    )}
  </label>
);

const ItemEditor = ({ it, onChange, MediaSlot }) => (
  <div className="rounded-xl border p-3 bg-white space-y-2" data-testid={`fp-item-${it.key}`}>
    <div className="flex items-center gap-2">
      <input type="number" value={it.order} onChange={(e) => onChange({ order: Number(e.target.value) })}
        className="w-16 px-2 py-1.5 border rounded text-sm" title="표시 순서" data-testid={`fp-item-${it.key}-order`} />
      <label className="flex items-center gap-1 text-xs font-bold">
        <input type="checkbox" checked={it.visible} onChange={(e) => onChange({ visible: e.target.checked })}
          data-testid={`fp-item-${it.key}-visible`} /> 공개
      </label>
    </div>
    <Field label="소분류 기능명" value={it.name} max={40} onChange={(v) => onChange({ name: v })} testid={`fp-item-${it.key}-name`} />
    <Field label="소분류 설명" value={it.desc} rows={3} max={400} onChange={(v) => onChange({ desc: v })} testid={`fp-item-${it.key}-desc`} />
    <div className="grid grid-cols-2 gap-2">
      <MediaSlot label="이미지" kind="image" removable value={it.image} onChange={(v) => onChange({ image: v })} testid={`fp-item-${it.key}-image`} />
      <MediaSlot label="영상" kind="video" removable value={it.video} onChange={(v) => onChange({ video: v })} testid={`fp-item-${it.key}-video`} />
    </div>
  </div>
);

// LIVEMOA-FEATURE-DETAIL-STEP1 · 기능 상세 페이지 콘텐츠 (현재 LIVE 통합 콘솔)
export const AdminFeaturePageEditor = ({ slug, path, MediaSlot, hideBenefits, taglineLabel = '대표 한 줄 소개' }) => {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/admin/feature-pages/${slug}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [slug]);
  if (!p) return <p className="text-sm text-gray-500">불러오는 중…</p>;
  const set = (patch) => setP((d) => ({ ...d, ...patch }));
  const setItem = (key, patch) => setP((d) => ({ ...d, items: d.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/feature-pages/${slug}`, p);
      setP(r.data);
      toast.success('저장 완료 · 상세 페이지에 반영되었습니다.');
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="admin-feature-page-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-black">{p.title} 상세 페이지</h2>
          <a href={path} target="_blank" rel="noreferrer" className="text-xs text-[#2F5BFF] inline-flex items-center gap-1">
            {path} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm font-bold">
            <input type="checkbox" checked={!!p.is_public} onChange={(e) => set({ is_public: e.target.checked })} data-testid="fp-public" /> 공개
          </label>
          <button type="button" onClick={save} disabled={saving} data-testid="fp-save-btn"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-[#2F5BFF] text-white text-sm font-bold disabled:opacity-50">
            <Save className="w-4 h-4" />{saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
      <div className="rounded-xl border p-4 bg-white grid md:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-3">
          <Field label="대표 제목" value={p.title} rows={2} max={600} onChange={(v) => set({ title: v })} testid="fp-title" />
          <Field label={taglineLabel} value={p.tagline} rows={2} max={600} onChange={(v) => set({ tagline: v })} testid="fp-tagline" />
          <Field label="대표 소개 문구" value={p.intro} rows={4} max={600} onChange={(v) => set({ intro: v })} testid="fp-intro" />
          {!hideBenefits && <Field label="주요 장점 (한 줄에 하나)" value={(p.benefits || []).join('\n')} rows={3}
            onChange={(v) => set({ benefits: v.split('\n') })} testid="fp-benefits" />}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
          <MediaSlot label="대표 이미지" kind="image" removable value={p.hero_image} onChange={(v) => set({ hero_image: v })} testid="fp-hero-image" />
          <MediaSlot label="대표 영상" kind="video" removable value={p.hero_video} onChange={(v) => set({ hero_video: v })} testid="fp-hero-video" />
        </div>
      </div>
      <div>
        <p className="text-sm font-bold mb-2">세부 기능 (표시 순서 · 공개 여부)</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {p.items.map((it) => <ItemEditor key={it.key} it={it} MediaSlot={MediaSlot} onChange={(patch) => setItem(it.key, patch)} />)}
        </div>
      </div>
    </div>
  );
};
